import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useConvenios, useContracts } from '../contexts/DataContext';
import { useUser } from '../components/UserContext';
import { aggregateProveedores } from '../utils/convenioCompliance';
import { Heading } from '../components/heading';
import { Text } from '../components/text';
import { Input } from '../components/input';
import { Button } from '../components/button';
import { Badge } from '../components/badge';
import {
  Table,
  TableHeader,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '../components/table';
import ConvenioCreateDialog from '../components/ConvenioCreateDialog';

const fmtMXN = (n) =>
  new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    maximumFractionDigits: 0,
  }).format(Number(n) || 0);

// Same tiles-as-filter pattern as the Comisiones page: one vocabulary for the
// whole page, one click to slice the list to the convenios with that problem.
function StatTile({ label, value, sub, tone, active, onClick }) {
  const tones = {
    red: 'text-red-700',
    amber: 'text-amber-700',
    default: 'text-azul-surelia',
  };
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      {...(onClick ? { type: 'button', onClick } : {})}
      className={`text-left bg-white rounded-lg border shadow-sm p-4 transition-all ${
        active
          ? 'border-azul-surelia ring-2 ring-azul-surelia/30'
          : onClick
          ? 'border-azul-surelia/15 hover:border-azul-surelia/40'
          : 'border-azul-surelia/15'
      }`}
    >
      <div className="text-[10px] uppercase tracking-wide text-gray-500">{label}</div>
      <div className={`text-xl font-bold ${tones[tone] || tones.default}`}>{value}</div>
      <div className="text-[11px] text-gray-500 mt-0.5">{sub || ' '}</div>
    </Tag>
  );
}

const EMPTY_STATS = {
  proveedores: 0,
  contratos: 0,
  overdue: 0,
  dueSoon: 0,
  vencidas: 0,
  vigentes: 0,
  pendientes: 0,
  montoTotal: 0,
  montoOverdue: 0,
  montoVencido: 0,
};

const Convenios = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const syncRef = useRef(false);
  const paramsHydratedRef = useRef(false);
  const { conveniosList, conveniosLoading, addConvenio } = useConvenios();
  const { contractsList } = useContracts();
  const { userData } = useUser();

  const [searchTerm, setSearchTerm] = useState('');
  const [attentionFilter, setAttentionFilter] = useState('todos');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);

  // Seed from URL
  useEffect(() => {
    if (syncRef.current) {
      syncRef.current = false;
      return;
    }
    const params = new URLSearchParams(location.search);
    const pageParam = parseInt(params.get('page') || '1', 10);
    if (!Number.isNaN(pageParam) && pageParam > 0) {
      setCurrentPage(pageParam);
    }
    paramsHydratedRef.current = true;
  }, [location.search]);

  // Keep URL in sync
  useEffect(() => {
    if (!paramsHydratedRef.current) return;
    const params = new URLSearchParams(location.search);
    params.set('page', currentPage);
    const newSearch = params.toString();
    const currentSearch = new URLSearchParams(location.search).toString();
    if (newSearch !== currentSearch) {
      syncRef.current = true;
      navigate({ pathname: '/app/convenios', search: newSearch }, { replace: true });
    }
  }, [currentPage, location.search, navigate]);

  // Reset page on search / filter
  useEffect(() => {
    if (paramsHydratedRef.current) setCurrentPage(1);
  }, [searchTerm, attentionFilter]);

  // Compliance rollup per convenio, from the same aggregation the detail
  // page's Proveedores tab uses — the list judges with the same numbers the
  // drill-down shows. Names (beneficiarios/proyectos) aren't needed at this
  // altitude, so the lookup maps stay empty.
  const statsByConvenio = useMemo(() => {
    const contractsByConvenio = new Map();
    contractsList.forEach((c) => {
      if (!c.convenioId) return;
      if (!contractsByConvenio.has(c.convenioId)) contractsByConvenio.set(c.convenioId, []);
      contractsByConvenio.get(c.convenioId).push(c);
    });
    const stats = {};
    conveniosList.forEach((cv) => {
      const agg = aggregateProveedores(contractsByConvenio.get(cv.id) || []);
      const s = { ...EMPTY_STATS, proveedores: agg.length };
      const contractIds = new Set();
      agg.forEach((p) => {
        p.contracts.forEach((id) => contractIds.add(id));
        s.overdue += p.counts.overdue;
        s.dueSoon += p.counts.dueSoon;
        s.vencidas += p.counts.vencida;
        s.vigentes += p.counts.vigente;
        s.pendientes += p.counts.pendiente;
        s.montoTotal += p.montos.total;
        s.montoOverdue += p.montos.overdue;
        s.montoVencido += p.montos.vencida;
      });
      s.contratos = contractIds.size;
      stats[cv.id] = s;
    });
    return stats;
  }, [contractsList, conveniosList]);

  const statsFor = useCallback(
    (id) => statsByConvenio[id] || EMPTY_STATS,
    [statsByConvenio]
  );

  // Org-wide totals for the tiles.
  const totals = useMemo(() => {
    const t = {
      overdue: 0, overdueConvenios: 0,
      dueSoon: 0, dueSoonConvenios: 0,
      vencidas: 0, vencidasConvenios: 0,
      montoTotal: 0, montoOverdue: 0, montoVencido: 0,
    };
    conveniosList.forEach((cv) => {
      const s = statsFor(cv.id);
      t.overdue += s.overdue;
      t.dueSoon += s.dueSoon;
      t.vencidas += s.vencidas;
      t.montoTotal += s.montoTotal;
      t.montoOverdue += s.montoOverdue;
      t.montoVencido += s.montoVencido;
      if (s.overdue > 0) t.overdueConvenios++;
      if (s.dueSoon > 0) t.dueSoonConvenios++;
      if (s.vencidas > 0) t.vencidasConvenios++;
    });
    return t;
  }, [conveniosList, statsFor]);

  const toggleAttention = (key) =>
    setAttentionFilter((f) => (f === key ? 'todos' : key));

  const filteredConvenios = useMemo(() => {
    let rows = conveniosList;
    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      rows = rows.filter((c) => {
        const nombre = c.nombre?.toLowerCase() || '';
        const desc = c.descripcion?.toLowerCase() || '';
        const beneficiarios = (c.beneficiarios || [])
          .map((b) => `${b.nombre} ${b.rfc}`.toLowerCase())
          .join(' ');
        return nombre.includes(lower) || desc.includes(lower) || beneficiarios.includes(lower);
      });
    }
    if (attentionFilter !== 'todos') {
      rows = rows.filter((c) => {
        const s = statsFor(c.id);
        if (attentionFilter === 'overdue') return s.overdue > 0;
        if (attentionFilter === 'dueSoon') return s.dueSoon > 0;
        if (attentionFilter === 'vencidas') return s.vencidas > 0;
        return true;
      });
    }
    // Urgency first — a convenio with money exposed sorts above a healthy one.
    return [...rows].sort((a, b) => {
      const sa = statsFor(a.id);
      const sb = statsFor(b.id);
      if (sa.montoOverdue !== sb.montoOverdue) return sb.montoOverdue - sa.montoOverdue;
      if (sa.overdue !== sb.overdue) return sb.overdue - sa.overdue;
      if (sa.dueSoon !== sb.dueSoon) return sb.dueSoon - sa.dueSoon;
      if (sa.vencidas !== sb.vencidas) return sb.vencidas - sa.vencidas;
      return sb.montoTotal - sa.montoTotal;
    });
  }, [conveniosList, searchTerm, attentionFilter, statsFor]);

  const totalPages = Math.ceil(filteredConvenios.length / itemsPerPage);
  const paginatedConvenios = filteredConvenios.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleCreate = async (data) => {
    setCreateLoading(true);
    try {
      await addConvenio(data);
      setIsCreateOpen(false);
    } catch (error) {
      console.error('Error creating convenio:', error);
    } finally {
      setCreateLoading(false);
    }
  };

  if (conveniosLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-azul-surelia" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <Heading>Convenios</Heading>
          <Text>Gestión de convenios de proveedores</Text>
        </div>
        {userData?.rol !== 'client' && (
          <Button onClick={() => setIsCreateOpen(true)}>
            Nuevo Convenio
          </Button>
        )}
      </div>

      {/* Attention tiles — click to slice the list */}
      {conveniosList.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <StatTile
            label="Emisiones atrasadas"
            value={totals.overdue}
            sub={totals.overdue > 0
              ? `${fmtMXN(totals.montoOverdue)} · ${totals.overdueConvenios} convenio${totals.overdueConvenios === 1 ? '' : 's'}`
              : 'Sin atrasos'}
            tone={totals.overdue > 0 ? 'red' : 'default'}
            active={attentionFilter === 'overdue'}
            onClick={() => toggleAttention('overdue')}
          />
          <StatTile
            label="Por emitir (30 días)"
            value={totals.dueSoon}
            sub={totals.dueSoon > 0
              ? `${totals.dueSoonConvenios} convenio${totals.dueSoonConvenios === 1 ? '' : 's'}`
              : 'Nada próximo'}
            tone={totals.dueSoon > 0 ? 'amber' : 'default'}
            active={attentionFilter === 'dueSoon'}
            onClick={() => toggleAttention('dueSoon')}
          />
          <StatTile
            label="Fianzas vencidas"
            value={totals.vencidas}
            sub={totals.vencidas > 0
              ? `${fmtMXN(totals.montoVencido)} · ${totals.vencidasConvenios} convenio${totals.vencidasConvenios === 1 ? '' : 's'}`
              : 'Sin vencidas'}
            tone={totals.vencidas > 0 ? 'red' : 'default'}
            active={attentionFilter === 'vencidas'}
            onClick={() => toggleAttention('vencidas')}
          />
          <StatTile
            label="Monto afianzado"
            value={fmtMXN(totals.montoTotal)}
            sub={`${conveniosList.length} convenio${conveniosList.length === 1 ? '' : 's'}`}
          />
        </div>
      )}

      {/* Search */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[240px]">
          <Input
            placeholder="Buscar por nombre, descripción o beneficiario..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        {attentionFilter !== 'todos' && (
          <button
            type="button"
            onClick={() => setAttentionFilter('todos')}
            className="text-xs text-azul-surelia hover:underline"
          >
            Quitar filtro de atención
          </button>
        )}
      </div>

      {filteredConvenios.length === 0 ? (
        <div className="text-center py-12">
          <Text>
            {conveniosList.length === 0
              ? 'No hay convenios registrados. Crea tu primer convenio.'
              : 'No se encontraron convenios con los filtros actuales.'}
          </Text>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-lg shadow-lg border border-azul-surelia/30 overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHead className="bg-gradient-to-r from-azul-surelia to-azul-surelia/95">
                  <TableRow>
                    <TableHeader className="text-white font-semibold py-4">Nombre</TableHeader>
                    <TableHeader className="text-white font-semibold py-4">Proveedores</TableHeader>
                    <TableHeader className="text-white font-semibold py-4">Cumplimiento</TableHeader>
                    <TableHeader className="text-white font-semibold py-4 text-right">Monto afianzado</TableHeader>
                    <TableHeader className="text-white font-semibold py-4">Estatus</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedConvenios.map((convenio, index) => {
                    const s = statsFor(convenio.id);
                    const hasProblems = s.overdue > 0 || s.dueSoon > 0 || s.vencidas > 0;
                    return (
                      <TableRow
                        key={convenio.id}
                        href={`/app/convenios/${convenio.id}`}
                        className={`cursor-pointer transition-all duration-200 hover:bg-azul-surelia/5 ${
                          index % 2 === 0 ? 'bg-white' : 'bg-beige-suave/15'
                        }`}
                      >
                        <TableCell className="py-4">
                          <span className="font-medium text-azul-surelia">
                            {convenio.nombre || 'Sin nombre'}
                          </span>
                          {convenio.descripcion && (
                            <p className="text-xs text-zinc-500 truncate max-w-xs mt-0.5">
                              {convenio.descripcion}
                            </p>
                          )}
                        </TableCell>
                        <TableCell className="py-4">
                          <span className="text-sm text-zinc-700 font-medium">{s.proveedores}</span>
                          <span className="text-xs text-zinc-500 ml-1.5">
                            · {s.contratos} contrato{s.contratos === 1 ? '' : 's'}
                          </span>
                        </TableCell>
                        <TableCell className="py-4">
                          <div className="flex flex-wrap items-center gap-1.5">
                            {s.overdue > 0 && (
                              <span
                                className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700"
                                title="Fianzas requeridas cuya fecha de emisión ya pasó."
                              >
                                {s.overdue} atrasada{s.overdue === 1 ? '' : 's'}
                              </span>
                            )}
                            {s.dueSoon > 0 && (
                              <span
                                className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700"
                                title="Fianzas requeridas que deben emitirse en los próximos 30 días."
                              >
                                {s.dueSoon} por emitir
                              </span>
                            )}
                            {s.vencidas > 0 && (
                              <span
                                className="text-[11px] font-medium px-2 py-0.5 rounded-full border border-red-300 text-red-700 bg-white"
                                title="Fianzas emitidas cuya vigencia ya terminó."
                              >
                                {s.vencidas} vencida{s.vencidas === 1 ? '' : 's'}
                              </span>
                            )}
                            {!hasProblems && s.contratos > 0 && (
                              <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                                Al corriente
                              </span>
                            )}
                            {!hasProblems && s.contratos === 0 && (
                              <span className="text-[11px] px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-500">
                                Sin contratos
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="py-4 text-right">
                          <span className="text-sm font-semibold text-zinc-800 tabular-nums">
                            {fmtMXN(s.montoTotal)}
                          </span>
                          {s.vigentes > 0 && (
                            <div className="text-[11px] text-zinc-500">
                              {s.vigentes} vigente{s.vigentes === 1 ? '' : 's'}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="py-4">
                          <Badge color={convenio.estatus === 'activo' ? 'green' : 'zinc'}>
                            {convenio.estatus === 'activo' ? 'Activo' : 'Inactivo'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <Text>
                Mostrando {(currentPage - 1) * itemsPerPage + 1} -{' '}
                {Math.min(currentPage * itemsPerPage, filteredConvenios.length)} de{' '}
                {filteredConvenios.length}
              </Text>
              <div className="flex gap-2">
                <Button
                  plain
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => p - 1)}
                >
                  Anterior
                </Button>
                <Button
                  plain
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((p) => p + 1)}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      <ConvenioCreateDialog
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onSubmit={handleCreate}
        loading={createLoading}
      />
    </div>
  );
};

export default Convenios;
